/**
 * Ensures a URL has a proper protocol (http:// or https://)
 * If the URL doesn't start with a protocol, it prepends https://
 * 
 * @param url - The URL to process
 * @returns The URL with a proper protocol
 */
export const ensureUrlProtocol = (url: string): string => {
  if (!url) return url;
  
  // Trim whitespace
  const trimmedUrl = url.trim();
  
  // If URL already has a protocol, return as is
  if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
    return trimmedUrl;
  }
  
  // If URL starts with //, it's a protocol-relative URL, add https:
  if (trimmedUrl.startsWith('//')) {
    return `https:${trimmedUrl}`;
  }
  
  // Otherwise, prepend https://
  return `https://${trimmedUrl}`;
};

/**
 * Opens a URL in a new tab, ensuring it has a proper protocol
 * 
 * @param url - The URL to open
 * @param target - The target window (default: "_blank")
 * @param features - Window features (default: undefined)
 */
export const openUrlSafely = (url: string, target: string = "_blank", features?: string): Window | null => {
  if (!url) return null;
  
  const safeUrl = ensureUrlProtocol(url);
  return window.open(safeUrl, target, features);
};